import React, { useState } from 'react';
import { X, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../routes';

interface BurgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSectionToggle = (id: string) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className={`fixed inset-0 z-50 pointer-events-none`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${isOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      {/* Menu */}
      <div
        className={`absolute left-0 top-0 h-full w-80 bg-theme-primary shadow-xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} pointer-events-auto`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-theme-primary">
            <h2 className="text-lg font-semibold text-theme-primary">Menu</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto py-4">
            {/* Dashboard */}
            <div className="px-4 mb-2">
              <button
                onClick={() => handleNavigation('/')} 
                className="w-full flex items-center px-3 py-2 text-left text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary rounded-md transition-colors"
              >
                <Home className="h-5 w-5 mr-3" />
                <span className="font-medium">Dashboard</span>
              </button>
            </div>
            {appRoutes.filter(item => item.id !== 'settings').map((item) => {
              const IconComponent = item.icon;
              const isOpen = openSection === item.id;
              return (
                <div key={item.id} className="px-4 mb-2">
                  <button
                    onClick={() => handleSectionToggle(item.id)}
                    className={`w-full flex items-center px-3 py-2 text-left text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary rounded-md transition-colors ${isOpen ? 'font-semibold' : ''}`}
                  >
                    <IconComponent className="h-5 w-5 mr-3" />
                    <span className="font-medium flex-1">{item.title}</span>
                    {item.children && <span className={`ml-2 transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>}
                  </button>
                  {/* Sub-items Accordion */}
                  {isOpen && item.children && (
                    <div className="ml-8 mt-1">
                      {item.children.map((child) => {
                        const ChildIconComponent = child.icon;
                        return (
                          <button
                            key={child.id}
                            onClick={() => handleNavigation(child.path)}
                            className="w-full flex items-center px-3 py-2 text-left text-theme-tertiary hover:text-theme-secondary hover:bg-theme-tertiary rounded-md text-sm transition-colors"
                          >
                            <ChildIconComponent className="h-4 w-4 mr-3" />
                            <span>{child.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Settings at bottom */}
          <div className="border-t border-theme-primary p-4">
            <button
              onClick={() => handleNavigation('/settings')}
              className="w-full flex items-center px-3 py-2 text-left text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary rounded-md transition-colors"
            >
              {(() => {
                const settingsRoute = appRoutes.find(r => r.id === 'settings');
                if (settingsRoute && settingsRoute.icon) {
                  const SettingsIcon = settingsRoute.icon;
                  return <SettingsIcon className="h-5 w-5 mr-3" />;
                }
                return null;
              })()}
              <span className="font-medium">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurgerMenu; 